import {PathHelper} from "@/utilities/helpers/PathHelper";
import {WebCallService} from "@/utilities/services/WebCallService";
import {HttpType, ErrorLocationType} from "@/utilities/enums/Enums";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import ProjectFile from "@/models/entities/ProjectFile";
import {Block} from "@/models/helpers/Block";
import {useGlobalStore} from "@/stores/GlobalStore";
import GeneralResponse from "@/models/api/GeneralResponse";
import GeneralRequest from "@/models/api/GeneralRequest";
import {UploadRequest} from "@/models/api/old/requests";


// noinspection DuplicatedCode
export default class FileService {
    private webCaller: WebCallService;
    private uriHelper: PathHelper;

    constructor(){
        this.webCaller = new WebCallService();
        this.uriHelper = new PathHelper();
    }

    public async getForUser(errorLocationType:ErrorLocationType): Promise<Array<ProjectFile>|null> {
        const uri = this.uriHelper.file.getFilesForUserPath();
        const requestBody = new GeneralRequest(new ProjectFile());

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as Array<ProjectFile>;
    }

    public async getBy(fileWithSearchProperty:ProjectFile, propertyNameToSearchBy:string, errorLocationType:ErrorLocationType):Promise<Array<ProjectFile>|null>{
        const uri = this.uriHelper.file.getByPath();
        const requestBody = new GeneralRequest(fileWithSearchProperty, propertyNameToSearchBy);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as Array<ProjectFile>;
    }

    public async update(file:ProjectFile, errorLocationType:ErrorLocationType):Promise<ProjectFile|null>{
        const uri = this.uriHelper.file.getUpdateFilePath();
        const requestBody = new GeneralRequest(file);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as ProjectFile;
    }

    public async delete(file:ProjectFile, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const uri = this.uriHelper.file.getDeleteFilePath();
        const requestBody = new GeneralRequest(file);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }

    public async resetDeletionDate(file:ProjectFile, errorLocationType:ErrorLocationType):Promise<ProjectFile|null>{
        const uri = this.uriHelper.file.getResetDeletionDatePath();
        const requestBody = new GeneralRequest(file);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as ProjectFile;
    }

    public async downloadFile(file:ProjectFile, callback:Function|null, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const globalStore = useGlobalStore();
        const uri = this.uriHelper.file.getDownloadFilePath();
        const requestBody = new GeneralRequest(file);

        globalStore.showProgress();

        const resp = await this.webCaller.downloadFileWithBody(uri, requestBody, callback);
        if (!resp || !resp.success) {
            globalStore.success.show = false;
            await ErrorHandler.handleOldError(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }

    public async downloadFileFromInternalUser(fileGuid:string, callback:Function|null, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const globalStore = useGlobalStore();
        const uri = this.uriHelper.file.getDownloadFileByMailPath(fileGuid);

        globalStore.showProgress(); //TODO expand with help text to avoid confusion

        const resp = await this.webCaller.downloadFile(uri, callback);
        if (!resp || !resp.success) {
            globalStore.success.show = false;
            await ErrorHandler.handleOldError(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }

    public async initializeUpload(file:ProjectFile, errorLocationType:ErrorLocationType):Promise<ProjectFile|null>{
        const uri = this.uriHelper.file.getInitializeUploadPath();
        const requestBody = new UploadRequest(file);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (!resp || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as ProjectFile;
    }

    public async uploadBlock(uploadGuid:string, block:Block, callback:Function|null = null, abortSignal:AbortSignal, errorLocationType:ErrorLocationType):Promise<string|null>{
        const uri = this.uriHelper.file.getUploadBlockPath(uploadGuid, block.index);

        const resp = await this.webCaller.uploadBlock(uploadGuid, block, uri, errorLocationType, callback, abortSignal);
        if (!resp || !(resp as any).success) {
            await ErrorHandler.handleResponse(resp as any, errorLocationType);
            return null;
        }

        return resp as string;
    }

    public async getMissingBlocks(uploadGuid:string, errorLocationType:ErrorLocationType):Promise<Array<number>|null>{
        const uri = this.uriHelper.file.getMissingBlocksPath(uploadGuid);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.get, uri);
        if (!resp || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as Array<number>;
    }

    public async abortUpload(uploadGuid:string, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const uri = this.uriHelper.file.getAbortUploadPath(uploadGuid);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri);
        if (!resp || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }

    public async completeUpload(uploadGuid:string, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const uri = this.uriHelper.file.getCompleteUploadPath(uploadGuid);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri);
        if (!resp || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }
}